(function () {
  const _s = {
    currentItemCount: 0,
    cartShareUrl: '',
    validated: true,
    openField: '',
    isOpenDiscountCodeMainCart: false,
    isCopiedCart: false,
    pendingUpdates: 0,
    // A refresh from the server is dropped when a newer one, or another cart change, started after it.
    refreshGeneration: 0,
    lastRefreshAt: 0,
  };

  const cartRoot = () => String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  /** A read of the header refresh that gives up after 10 seconds instead of hanging the refresh. */
  const timedFetch = (url, options) => {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 10000);
    return fetch(url, {...options, signal: controller.signal}).finally(() => window.clearTimeout(timer));
  };
  /** What /cart.js says about the lines, in the form the page renders (snippets/bestio-cart-bubble.liquid). */
  const cartSignature = cart => (cart?.items || []).map(item => `${item.key}:${item.quantity},`).join('');
  const renderedBubble = () => document.querySelector('#cart-icon-bubble .cart-bubble__container');

  /**
   * The checkout page under the current language subfolder. The paths come from the market contract
   * (bestio-checkout-market.js); should that script be missing, the button still leads to the checkout.
   */
  window.bestioCheckoutUrl = () => {
    const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    const paths = window.BestioCheckoutMarket?.checkoutPaths(root);
    if (!paths) console.error('Bestio checkout market contract is not loaded; using the default checkout path.');
    return paths?.checkout ?? `${root}pages/checkout-details`;
  };

  function _dispatch(name, detail) {
    document.dispatchEvent(new CustomEvent(name, {detail}));
  }

  function initCartIconFallback() {
    const cartIcon = document.getElementById('cart-icon');
    if (!cartIcon) return;
    let closeTimer;
    const miniCart = () => document.getElementById('BestioMiniCart');
    const positionMiniCart = () => {
      const popup = miniCart();
      if (!popup) return;
      const iconRect = cartIcon.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - iconRect.right);
      popup.style.setProperty('--bestio-mini-cart-right', `${right}px`);
      popup.style.setProperty('--bestio-mini-cart-top', `${Math.round(iconRect.bottom + 12)}px`);
    };
    const openMiniCart = () => {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      window.clearTimeout(closeTimer);
      const popup = miniCart();
      if (!popup) return;
      positionMiniCart();
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      cartIcon.setAttribute('aria-expanded', 'true');
    };
    const closeMiniCart = (delay = 120) => {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        const popup = miniCart();
        popup?.classList.remove('is-open');
        popup?.setAttribute('aria-hidden', 'true');
        cartIcon.setAttribute('aria-expanded', 'false');
      }, delay);
    };

    cartIcon.addEventListener('mouseenter', openMiniCart);
    cartIcon.addEventListener('mouseleave', () => closeMiniCart());
    cartIcon.addEventListener('focus', openMiniCart);
    document.addEventListener('mouseover', event => {
      if (event.target.closest('#BestioMiniCart')) openMiniCart();
    });
    document.addEventListener('mouseout', event => {
      const popup = event.target.closest('#BestioMiniCart');
      if (popup && !popup.contains(event.relatedTarget)) closeMiniCart();
    });
    document.addEventListener('focusin', event => {
      if (event.target.closest('#BestioMiniCart')) openMiniCart();
    });
    document.addEventListener('focusout', event => {
      const popup = event.target.closest('#BestioMiniCart');
      if (popup && !popup.contains(event.relatedTarget)) closeMiniCart();
    });
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') closeMiniCart(0);
    });
    window.addEventListener('resize', () => {
      if (miniCart()?.classList.contains('is-open')) positionMiniCart();
    });
    window.addEventListener('scroll', () => closeMiniCart(0), {passive: true});
  }

  window.xCartHelper = {
    items: [],

    get currentItemCount() { return _s.currentItemCount; },
    set currentItemCount(v) { _s.currentItemCount = v; },

    get validated() { return _s.validated; },
    set validated(v) { _s.validated = v; },

    get isOpenDiscountCodeMainCart() { return _s.isOpenDiscountCodeMainCart; },
    set isOpenDiscountCodeMainCart(v) { _s.isOpenDiscountCodeMainCart = v; },

    get openField() { return _s.openField; },
    set openField(v) {
      _s.openField = v;
      _dispatch('cart:fieldchange', {field: v});
    },

    get isCopiedCart() { return _s.isCopiedCart; },
    set isCopiedCart(v) {
      _s.isCopiedCart = v;
      _dispatch('cart:copiedchange', {copied: v});
    },

    get isUpdating() { return _s.pendingUpdates > 0; },
    set isUpdating(value) {
      const checkoutBtn = document.querySelector(".cart-drawer__checkout");
      const viewCartBtn = document.querySelector(".cart-drawer__view-cart");
      if (viewCartBtn) viewCartBtn.ariaDisabled = value;
      if (checkoutBtn) checkoutBtn.ariaDisabled = value;
      _s.isUpdating = value;
      },
    beginUpdate() { _s.pendingUpdates++; },
    endUpdate() { _s.pendingUpdates = Math.max(0, _s.pendingUpdates - 1); },

    get cartShareUrl() { return _s.cartShareUrl; },
    set cartShareUrl(v) {
      _s.cartShareUrl = v;
      _dispatch('cart:shareurlchange', {url: v});
    },

    // ── Cart API ──────────────────────────────────────────────────────────

    /**
     * Waits, briefly, for the visit source to be written into the cart before the cart changes (bestio-visit-source.js,
     * BEST-615): a new visitor's write and first add could otherwise each create a cart. Never longer than its timeout.
     */
    visitSourceReady() {
      return window.BestioVisitSource?.ready?.() ?? Promise.resolve();
    },

    addItems(items) {
      const cartDrawerEl = document.querySelector('#CartDrawer');
      const sectionId = cartDrawerEl?.dataset.sectionId;
      const sectionsToRender = this.getSectionsToRender(sectionId);
      const sections = sectionsToRender.map(s => s.id);
      this.isUpdating = true;

      this.visitSourceReady().then(() => fetch(Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({items, sections}),
      }))
        .then(r => r.json())
        .then(response => {
          sectionsToRender.forEach(section => {
            const el = document.querySelector(section.selector);
            if (el && response.sections[section.id]) {
              el.innerHTML = getSectionInnerHTML(response.sections[section.id], section.selector);
            }
          });
          document.dispatchEvent(new CustomEvent('cart:add-confirmation:open', {
            detail: {variantId: String(response.items?.[0]?.variant_id || response.items?.[0]?.id || '')}
          }));
        })
        .catch(e => console.error(e))
        .finally(() => {
          this.isUpdating = false;
        });
    },

    updateCart(data, needValidate = false) {
      this.isUpdating = true;
      return this.visitSourceReady().then(() => fetch(Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify(data),
      })).then(() => {
        if (needValidate) this.validateCart();
      }).finally(() => {
        this.isUpdating = false;
      });
    },

    changeCart(data) {
      return this.visitSourceReady().then(() => fetch(Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(data),
      })).then(r => r.text());
    },

    // ── Validation ────────────────────────────────────────────────────────

    validateCart() {
      this.validated = true;

      const carTerm = document.querySelector(`cart-term`);
      if (carTerm) this.validated = carTerm._isChecked;
      document.dispatchEvent(new CustomEvent(CART_EVENT.validate));
    },

    goToCheckout(e) {
      if (this.isUpdating) {
        e.preventDefault();
        return;
      }
      this.validateCart();

      if (this.validated) {
        e.preventDefault();
        fetch(Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({
            attributes: {
              'collection-pagination': null,
              'blog-pagination': null,
              'choose_option_id': null,
              'datetime-updated': null,
              'datetime-created': null,
              [CART_ATTRIBUTES.product_comparison_options]: null,
              [CART_ATTRIBUTES.product_comparison_metafields]: null,
            },
          }),
        }).finally(() => {
          // A checkout session is created only after the cart has settled and the customer reaches the first
          // checkout page. The page keeps the language subfolder (/en/), so the checkout and the order it creates
          // stay in the customer's language.
          window.location.assign(window.bestioCheckoutUrl());
        });
      } else {
        e.preventDefault();
      }
    },

    // ── Sections ──────────────────────────────────────────────────────────

    getSectionsToRender(sectionId) {
      const sectionsToRender = [
        {id: 'cart-icon-bubble', selector: '#cart-icon-bubble'},
        {id: `${sectionId}`, selector: '.cart__shipping-and-delivery-wrapper'},
      ];

      if (window.Maximize.isCartPage) {
        sectionsToRender.push({id: `${sectionId}`, selector: '#MainCart__Items'});
      } else if (sectionId) {
        sectionsToRender.push({id: `${sectionId}`, selector: '#CartDrawer'});
      }

      return sectionsToRender;
    },

    /**
     * The header, the mini cart and the cart page from the cart Shopify holds now (BEST-542). The page is rendered on the
     * server with the cart of that moment, so a page from the back-forward cache, an order paid elsewhere or a change in
     * another tab left them showing another cart until the next add showed everything at once. Reads /cart.js past every
     * cache and renders the sections again only when the count or the lines differ from what the page shows. Resolves
     * with the cart, or null when it could not be read.
     */
    async refreshFromServer() {
      const generation = ++_s.refreshGeneration;
      _s.lastRefreshAt = Date.now();
      let cart;
      try {
        const response = await timedFetch(`${cartRoot()}cart.js`, {headers: {Accept: 'application/json'}, credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw new Error(`cart.js answered ${response.status}`);
        cart = await response.json();
      } catch (error) {
        console.warn('Bestio cart could not be read for the header.', {reason: error?.message || null});
        return null;
      }
      // Without a cart icon (the theme hides it) there is nothing to compare; a page rendered before the signature existed
      // is rendered again.
      const bubble = renderedBubble();
      if (!bubble || generation !== _s.refreshGeneration) return cart;
      if (Number(bubble.dataset.countItem || 0) === cart.item_count && bubble.dataset.bestioCartSignature === cartSignature(cart)) return cart;
      const sections = [{id: 'cart-icon-bubble', selector: '#cart-icon-bubble'}];
      const drawer = document.getElementById('CartDrawer');
      const mainCart = document.getElementById('MainCart__Items');
      if (drawer?.dataset.sectionId) {
        sections.push({id: drawer.dataset.sectionId, selector: '#CartDrawer'});
        sections.push({id: drawer.dataset.sectionId, selector: '.cart__shipping-and-delivery-wrapper'});
      }
      if (mainCart?.dataset.sectionId) sections.push({id: mainCart.dataset.sectionId, selector: '#MainCart__Items'});
      let rendered;
      try {
        const ids = [...new Set(sections.map(section => section.id))].join(',');
        const response = await timedFetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`, {credentials: 'same-origin', cache: 'no-store'});
        if (!response.ok) throw new Error(`sections answered ${response.status}`);
        rendered = await response.json();
      } catch (error) {
        console.warn('Bestio cart sections could not be rendered again.', {reason: error?.message || null});
        return cart;
      }
      if (generation !== _s.refreshGeneration) return cart;
      sections.forEach(section => {
        const target = document.querySelector(section.selector);
        const html = rendered?.[section.id] ? getSectionInnerHTML(rendered[section.id], section.selector) : undefined;
        if (!target || html === undefined) return;
        // An open drawer stays open (cart-drawer.js keeps its wrapper visible the same way).
        const wrapperVisible = section.selector === '#CartDrawer' && target.querySelector('#CartDrawer__Wrapper')?.classList.contains('is-visible');
        target.innerHTML = html;
        if (wrapperVisible) target.querySelector('#CartDrawer__Wrapper')?.classList.add('is-visible');
      });
      // What the drawer does after its own redraw: the delivery estimate of the new lines.
      drawer?._initEstimateShipping?.();
      this.currentItemCount = cart.item_count;
      document.dispatchEvent(new CustomEvent(CART_EVENT.cartUpdate, {
        bubbles: true,
        detail: {isItemCountChanged: true, itemCount: cart.item_count, source: 'bestio-cart-sync'},
      }));
      return cart;
    },

    // ── Share cart ────────────────────────────────────────────────────────

    generateUrl() {
      fetch(Shopify.routes.root + 'cart.js', {
        method: 'GET',
        headers: {'Content-Type': 'application/json'},
      })
        .then(r => r.json())
        .then(response => {
          this.items = response.items;
          const serialize = (obj) =>
            Object.entries(obj)
              .reduce((str, [k, v]) => str.concat(`${encodeURIComponent(k)}:${encodeURIComponent(v.toString())},`), '')
              .slice(0, -1);

          const filteredCart = this.items.map(item => ({id: item.variant_id, quantity: item.quantity}));
          filteredCart.push({share_cart: true});
          this.cartShareUrl = `${window.location.origin}?&${filteredCart.map(serialize).join('&')}`;
        });
    },

    copyURL() {
      const input = document.querySelector('#cart-share');
      if (input) {
        input.select();
        document.execCommand('copy');
        this.isCopiedCart = true;
      }
    },

    handleSharedCart() {
      if (!location.search.includes('share_cart')) return;
      const queryString = window.location.search.substring(1);
      if (!queryString.includes('share_cart:true')) return;

      const pairs = queryString.match(/id:\d+,quantity:\d+/g);
      if (!pairs) return;

      const items = pairs.map(pair => {
        const [idPart, qtyPart] = pair.split(',');
        return {id: Number(idPart.split(':')[1]), quantity: Number(qtyPart.split(':')[1])};
      });

      if (items.length) this.addItems(items);
    },

    // ── UI helpers ────────────────────────────────────────────────────────

    updateCurrentCountItem() {
      const container = document.querySelector('#cart-icon-bubble .cart-bubble__container');
      this.currentItemCount = parseInt(container?.dataset.countItem || 0);
    },

    closeField(isCloseMiniCart = false) {
      this.isCopiedCart = false;
      if (isCloseMiniCart) {
        setTimeout(() => { this.openField = ''; }, DURATION.DRAWER_TRANSITION);
      } else {
        this.openField = '';
      }
    },
  };

  initCartIconFallback();
  // Initialize currentItemCount from server-rendered DOM (script runs deferred, DOM is ready)
  window.xCartHelper.updateCurrentCountItem();

  // The header follows the real cart (BEST-542): on load, on a page restored from the back-forward cache and when the tab
  // becomes visible again (at most every two seconds). Any other cart change drops a refresh still under way.
  document.addEventListener(CART_EVENT.cartUpdate, event => {
    if (event.detail?.source !== 'bestio-cart-sync') _s.refreshGeneration++;
  });
  const refresh = () => window.xCartHelper.refreshFromServer();
  refresh();
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - _s.lastRefreshAt >= 2000) refresh();
  });
})();
